import { DetailsType, FormData, Attachment, FieldValue } from 'types/application';

import {
    SECT_TYPE,
    SECT_FILER,
    SECT_APPLICANT,
    SECT_PE,
    SECT_EVENT_DETAILS,
    SECT_EVENT_VENUE,
    SECT_PERFORMER,
    SECT_DOCS
} from 'common/application.const';
import {
    formatSavePlatformsToBeUsed,
    retrievePlatforms,
    attachmentActions,
    attachmentPayloadSetting,
    attachmentRetrieve,
    tableActions,
    payloadDeleteApplication,
    formDeleteApplication
} from '../helpers';
import { formatDate } from 'utils/application.helpers';

import { decryptId } from 'common/decrypt';

type Props = {
    myInfoContent: any;
    uen?: string;
    id?: string;
};

const autoFillData = async ({ myInfoContent, uen, id }: Props) => {
    // to decrypt user id in local storage
    const decryptedID = await decryptId(id || '', 'decrypt');
    const decryptedUEN = await decryptId(uen || '', 'decrypt');
    const person = myInfoContent?.corppass || myInfoContent || {};
    const entity = myInfoContent?.['basic-profile'] || {};
    const corpUser = person?.['corppass-user'] || {};
    const address = myInfoContent?.address || person?.regadd || {};

    const isFin = ['F', 'G', 'M'].some((pre: string) => person?.uinfin?.value?.startsWith(pre));

    let idType = '';
    if (isFin) idType = 'FIN';
    if (person?.nationality?.code && !isFin) {
        idType = person.nationality.code === 'SG' ? 'NRC' : 'NRP';
    }

    let salutation = '';
    if (person.sex) {
        salutation = person.sex?.code === 'M' ? 'Mr.' : 'Ms.';
    }

    let corpUserID = corpUser['id-type']?.desc;
    if (corpUserID === 'NRIC') corpUserID = 'NRC';

    const generalForm = {
        id: 'general',
        formID: 'general',
        hasError: true,
        values: {
            [`${SECT_FILER}-id-type`]: corpUserID || idType,
            [`${SECT_FILER}-idno`]: decryptedID,
            [`${SECT_FILER}-salutaion`]: salutation,
            [`${SECT_FILER}-name`]: corpUser.name?.value || person?.name?.value,
            [`${SECT_FILER}-mobile-no-country-code`]: person?.mobileno?.areacode?.value || '65',
            [`${SECT_FILER}-mobile-no-main`]: person?.mobile?.value || person?.mobileno?.nbr?.value,
            [`${SECT_FILER}-office-no-country-code`]: '65',
            [`${SECT_FILER}-office-no-main`]: '',
            [`${SECT_FILER}-email`]: person?.email?.value,
            [`${SECT_APPLICANT}-id-type`]: corpUserID || idType,
            [`${SECT_APPLICANT}-idno`]: decryptedID,
            [`${SECT_APPLICANT}-salutaion`]: salutation,
            [`${SECT_APPLICANT}-name`]: corpUser.name?.value || person?.name?.value,
            // [`${SECT_APPLICANT}-bday`]: formatDate(content?.dob?.value),
            [`${SECT_APPLICANT}-nationality`]: person?.nationality?.code,
            [`${SECT_APPLICANT}-uen-no`]: decryptedUEN,
            [`${SECT_APPLICANT}-company`]: entity['entity-name']?.value,
            [`${SECT_APPLICANT}-mobile-no-country-code`]: person?.mobileno?.areacode?.value || '65',
            [`${SECT_APPLICANT}-mobile-no-main`]: person?.mobile?.value || person?.mobileno?.nbr?.value,
            [`${SECT_APPLICANT}-office-no-country-code`]: '65',
            [`${SECT_APPLICANT}-office-no-main`]: '',
            [`${SECT_APPLICANT}-email`]: person?.email?.value,
            [`${SECT_APPLICANT}-address-type`]: address.type || address.addresstype || 'SG',
            [`${SECT_APPLICANT}-postal`]: address.postal?.value,
            // [`${SECT_APPLICANT}-block-house`]: address.block?.value,
            // [`${SECT_APPLICANT}-street`]: address.street?.value,
            [`${SECT_APPLICANT}-floor`]: address.floor?.value,
            [`${SECT_APPLICANT}-unit`]: address.unit?.value
            // [`${SECT_APPLICANT}-building`]: address.building?.value
        }
    };

    return generalForm;
};

const formatRetrieve = async (content: any, action?: string) => {
    if (action === 'application-delete') return formDeleteApplication;
    const filerValues = content.Filer || {};
    const filerContact = filerValues.Contact || {};
    const applicantValues = content.Applicant || {};
    const applicantContact = applicantValues.Contact || {};
    const applicantAddress =
        applicantValues.Address && applicantValues.Address.length > 0 ? applicantValues.Address[0]?.MailingAddress || {} : {};
    const generalForm = {
        id: 'general',
        formID: 'general',
        hasError: true,
        values: {
            [`${SECT_TYPE}-applicant-type`]: content.AppliedAs || 'Applicant',
            [`${SECT_FILER}-id-type`]: filerValues.IDType,
            [`${SECT_FILER}-idno`]: filerValues.IDNo,
            [`${SECT_FILER}-salutaion`]: filerValues.Salutation,
            [`${SECT_FILER}-name`]: filerValues.Person?.pyFullName,
            [`${SECT_FILER}-mobile-no-country-code`]: filerContact.MobileNoCountryCode || '65',
            [`${SECT_FILER}-mobile-no-main`]: filerContact.MobileNo,
            [`${SECT_FILER}-office-no-country-code`]: filerContact.OfficeNoCountryCode || '65',
            [`${SECT_FILER}-office-no-main`]: filerContact.OfficeNo,
            [`${SECT_FILER}-email`]: filerContact.EmailAddress,
            [`${SECT_APPLICANT}-id-type`]: applicantValues.IDType,
            [`${SECT_APPLICANT}-idno`]: applicantValues.IDNo,
            [`${SECT_APPLICANT}-salutaion`]: applicantValues.Salutation,
            [`${SECT_APPLICANT}-name`]: applicantValues.Person?.pyFullName,
            [`${SECT_APPLICANT}-bday`]: formatDate(applicantValues.DateOfBirth),
            [`${SECT_APPLICANT}-nationality`]: applicantValues.Nationality,
            [`${SECT_APPLICANT}-uen-no`]: applicantValues.CompanyUEN,
            [`${SECT_APPLICANT}-company`]: applicantValues.CompanyName,
            [`${SECT_APPLICANT}-mobile-no-country-code`]: applicantContact.MobileNoCountryCode || '65',
            [`${SECT_APPLICANT}-mobile-no-main`]: applicantContact.MobileNo,
            [`${SECT_APPLICANT}-office-no-country-code`]: applicantContact.OfficeNoCountryCode || '65',
            [`${SECT_APPLICANT}-office-no-main`]: applicantContact.OfficeNo,
            [`${SECT_APPLICANT}-email`]: applicantContact.EmailAddress,
            [`${SECT_APPLICANT}-address-type`]: applicantAddress.AddressType || 'SG',
            [`${SECT_APPLICANT}-postal`]: applicantAddress.PostalCode,
            [`${SECT_APPLICANT}-block-house`]: applicantAddress.BlockNumber,
            [`${SECT_APPLICANT}-street`]: applicantAddress.StreetName,
            [`${SECT_APPLICANT}-floor`]: applicantAddress.FloorNumber,
            [`${SECT_APPLICANT}-unit`]: applicantAddress.UnitNumber,
            [`${SECT_APPLICANT}-building`]: applicantAddress.BuildingName
        }
    };

    const eventsForm = await Promise.all(
        content?.EventList?.map(async (event: any, index: number) => {
            const peValues = event.PELicence || {};
            const capacityValues = event.Capacity || {};
            const dateTimeValues = (event.DateAndTime || []).map((dateTime: { [key: string]: string }) => ({
                uploaded: 'true',
                from: dateTime.StartDateTime,
                to: dateTime.EndDateTime
            }));
            const performerValues = (event.Performer || []).map((performer: { [key: string]: string }) => ({
                artist: performer.Name,
                country: performer.Nationality,
                'group-band': performer.GroupBandName,
                uploaded: 'true'
            }));
            const eventAddress = event.Address && event.Address.length > 0 ? event.Address[0]?.MailingAddress || {} : {};
            const { Facebook, SISITICLive, Zoom, Youtube, Others } = event;
            const formattedPlatform = retrievePlatforms({ Facebook, SISITICLive, Zoom, Youtube, Others });
            return {
                id: `event-${index + 1}`,
                formID: 'event',
                hasError: true,
                values: {
                    [`${SECT_PE}-pe-no`]: peValues.PELicenceNo,
                    [`${SECT_PE}-pe-type`]: peValues.PELicenceType,
                    [`${SECT_PE}-cat1`]: peValues.Category1Public,
                    [`${SECT_PE}-pe-start`]: peValues.PELicenceStartDate,
                    [`${SECT_PE}-pe-end`]: peValues.PELicenceEndDate,
                    [`${SECT_EVENT_DETAILS}-ref-no`]: event.caseid,
                    [`${SECT_EVENT_DETAILS}-event-title`]: event.Title,
                    [`${SECT_EVENT_DETAILS}-event-type`]: event.Type,
                    [`${SECT_EVENT_DETAILS}-event-sub-type`]: event.SubType,
                    [`${SECT_EVENT_DETAILS}-sub-type-desc`]: event.SubTypeDescription,
                    [`${SECT_EVENT_DETAILS}-language`]: event.Language,
                    [`${SECT_EVENT_DETAILS}-lang-desc`]: event.LanguageDescription,
                    [`${SECT_EVENT_DETAILS}-event-desc`]: event.Description,
                    [`${SECT_EVENT_DETAILS}-offered-online`]: event.OfferedOnlineOrVOD,
                    [`${SECT_PERFORMER}-provide-performer-list`]: event.WishToProvidePerformersList,
                    [`${SECT_EVENT_DETAILS}-online-platform`]: formattedPlatform,
                    [`${SECT_EVENT_DETAILS}-ticketed-or-free`]: event.OnlineComponent,
                    [`${SECT_EVENT_DETAILS}-online-platform-others`]: event.OtherPlatform,
                    [`${SECT_EVENT_DETAILS}-in-out`]: capacityValues.IndoorOutdoor,
                    [`${SECT_EVENT_DETAILS}-event-date-time`]: dateTimeValues,
                    [`${SECT_EVENT_DETAILS}-admission-fee`]: capacityValues.AdmissionFee,
                    [`${SECT_EVENT_DETAILS}-crowd-size`]: capacityValues.CrowdSize,
                    [`${SECT_EVENT_VENUE}-address-type`]: eventAddress.AddressType || 'SG',
                    [`${SECT_EVENT_VENUE}-postal`]: eventAddress.PostalCode,
                    [`${SECT_EVENT_VENUE}-block-house`]: eventAddress.BlockNumber,
                    [`${SECT_EVENT_VENUE}-street`]: eventAddress.StreetName,
                    [`${SECT_EVENT_VENUE}-floor`]: eventAddress.FloorNumber,
                    [`${SECT_EVENT_VENUE}-unit`]: eventAddress.UnitNumber,
                    [`${SECT_EVENT_VENUE}-building`]: eventAddress.BuildingName,
                    [`${SECT_EVENT_VENUE}-location-desc`]: eventAddress.LocationDescription,
                    [`${SECT_EVENT_VENUE}-address1`]: eventAddress.AddressLine1,
                    [`${SECT_EVENT_VENUE}-address2`]: eventAddress.AddressLine2,
                    [`${SECT_PERFORMER}-performer-list`]: performerValues,
                    [`${SECT_PERFORMER}-performerListDocs`]: attachmentRetrieve(event?.PerformerListDocs),
                    [`${SECT_DOCS}-docs-list-0-link`]: event?.Link,
                    [`${SECT_DOCS}-docs-list-0-docs`]: attachmentRetrieve(event?.SupportingDocuments),
                    [`${SECT_DOCS}-docs-list-1-link`]: event?.LinkOthers,
                    [`${SECT_DOCS}-docs-list-1-docs`]: attachmentRetrieve(event?.SupportingDocumentsOthers)
                }
            };
        })
    );

    return [generalForm, ...(eventsForm || [])];
};

const formatSave = async (details: DetailsType, caseTypeID: string, pageInstructions: any[] = [], userRole?: string, action?: string) => {
    if (action === 'application-delete') return payloadDeleteApplication;
    const general = details.forms.find((form: FormData) => form.id === 'general')?.values || {};
    const generalIDno =
        general['applicant-type-section-applicant-type'] === 'Applicant'
            ? general[`${SECT_APPLICANT}-idno`]
            : general[`${SECT_FILER}-idno`];
    const generalBDay = general[`${SECT_APPLICANT}-bday`] as string;

    const events = details.forms.filter((form: FormData) => form.formID === 'event');

    // let filesToUpload: Attachment[] = [];
    // let filesToUploadPerformer: Attachment[] = [];
    let pageInstructionsForDelete: any[] = [];

    await Promise.all(
        events.map(async (event: FormData, eventIdx: number) => {
            const eventValues = event?.values || {};
            const attachments0 = (eventValues[`${SECT_DOCS}-docs-list-0-docs`] as Attachment[]) || [];
            const attachments1 = (eventValues[`${SECT_DOCS}-docs-list-1-docs`] as Attachment[]) || [];
            const performers = (eventValues[`${SECT_PERFORMER}-performerListDocs`] as Attachment[]) || [];
            const dateAndTime = (eventValues[`${SECT_EVENT_DETAILS}-event-date-time`] as { [key: string]: FieldValue }[]) || [];
            const performersList = (eventValues[`${SECT_PERFORMER}-performer-list`] as { [key: string]: FieldValue }[]) || [];

            const performerInstructions = tableActions(performersList, `EventList(${eventIdx + 1}).Performer`);
            const dateAndTimeInstructions = tableActions(dateAndTime, `EventList(${eventIdx + 1}).DateAndTime`);

            const attachments0Action = await attachmentActions(attachments0, `EventList(${eventIdx + 1}).SupportingDocuments`, caseTypeID);
            const attachments1Action = await attachmentActions(
                attachments1,
                `EventList(${eventIdx + 1}).SupportingDocumentsOthers`,
                caseTypeID
            );
            const performersAction = await attachmentActions(performers, `EventList(${eventIdx + 1}).PerformerListDocs`, caseTypeID);

            // filesToUpload = [...filesToUpload, ...attachments0Action.toUpload, ...attachments1Action.toUpload];
            // filesToUploadPerformer = [...filesToUploadPerformer, ...performersAction.toUpload];
            pageInstructionsForDelete = [
                ...pageInstructionsForDelete,
                ...performerInstructions,
                ...dateAndTimeInstructions,
                ...attachments0Action.pageInstructions,
                ...attachments1Action.pageInstructions,
                ...performersAction.pageInstructions
            ];
        })
    );

    // const filesToAttach = await requestUpload(filesToUpload, 'SupportingDocuments');
    // const filesToAttachPerformer = await requestUpload(filesToUploadPerformer, 'PerformerDetails');

    // if (filesToAttach.length > 0) await requestAttach(filesToAttach, caseTypeID);
    // if (filesToAttachPerformer.length > 0) await requestAttach(filesToAttachPerformer, caseTypeID);

    // const uploadedFiles = (caseTypeID ? await requestAttachmentList(caseTypeID) : []) as AttachPayload[];

    return {
        caseTypeID: 'IMDA-IRIS-Work-Arts',
        content: {
            ...(userRole === 'singpass' && { IDNo: generalIDno }),
            LoginType: userRole,
            AppliedAs: general['applicant-type-section-applicant-type'],
            Filer: {
                pxObjClass: 'IMDA-Data-Applicant',
                IDNo: general[`${SECT_FILER}-idno`],
                IDType: general[`${SECT_FILER}-id-type`],
                Salutation: general[`${SECT_FILER}-salutaion`],
                Address: [
                    {
                        pxObjClass: 'IMDA-Data-Address',
                        MailingAddress: {
                            pxObjClass: 'IMDA-Data-Address'
                        }
                    }
                ],
                Contact: {
                    pxObjClass: 'IMDA-Data-Contact',
                    EmailAddress: general[`${SECT_FILER}-email`],
                    MobileNoCountryCode: general[`${SECT_FILER}-mobile-no-country-code`],
                    MobileNo: general[`${SECT_FILER}-mobile-no-main`],
                    OfficeNoCountryCode: general[`${SECT_FILER}-office-no-country-code`],
                    OfficeNo: general[`${SECT_FILER}-office-no-main`]
                },
                Person: {
                    pxObjClass: 'IMDA-Data-Person',
                    pyFullName: general[`${SECT_FILER}-name`]
                }
            },
            Applicant: {
                pxObjClass: 'IMDA-Data-Applicant',
                CopyFilerDetails: 'true',
                CompanyName: general[`${SECT_APPLICANT}-company`],
                CompanyUEN: general[`${SECT_APPLICANT}-uen-no`],
                DateOfBirth: generalBDay?.replace(/\//g, '-'),
                IDNo: general[`${SECT_APPLICANT}-idno`],
                IDType: general[`${SECT_APPLICANT}-id-type`],
                Nationality: general[`${SECT_APPLICANT}-nationality`],
                Salutation: general[`${SECT_APPLICANT}-salutaion`],
                Address: [
                    {
                        pxObjClass: 'IMDA-Data-Address',
                        MailingAddress: {
                            pxObjClass: 'IMDA-Data-Address',
                            AddressType: general[`${SECT_APPLICANT}-address-type`],
                            BlockNumber: general[`${SECT_APPLICANT}-block-house`],
                            BuildingName: general[`${SECT_APPLICANT}-building`],
                            FloorNumber: general[`${SECT_APPLICANT}-floor`],
                            UnitNumber: general[`${SECT_APPLICANT}-unit`],
                            PostalCode: general[`${SECT_APPLICANT}-postal`],
                            StreetName: general[`${SECT_APPLICANT}-street`]
                        }
                    }
                ],
                Contact: {
                    pxObjClass: 'IMDA-Data-Contact',
                    EmailAddress: general[`${SECT_APPLICANT}-email`],
                    MobileNoCountryCode: general[`${SECT_APPLICANT}-mobile-no-country-code`],
                    MobileNo: general[`${SECT_APPLICANT}-mobile-no-main`],
                    OfficeNoCountryCode: general[`${SECT_APPLICANT}-office-no-country-code`],
                    OfficeNo: general[`${SECT_APPLICANT}-office-no-main`]
                },
                Person: {
                    pxObjClass: 'IMDA-Data-Person',
                    pyFullName: general[`${SECT_APPLICANT}-name`]
                }
            },
            DraftTitle: events.map((event: FormData) => (event.values ? event.values[`${SECT_EVENT_DETAILS}-event-title`] : '')).join(', '),
            EventList: events.map((event: FormData) => {
                const eventValues = event?.values || {};
                const performers = (eventValues[`${SECT_PERFORMER}-performer-list`] as { [key: string]: string }[]) || [];
                const dateAndTime = (eventValues[`${SECT_EVENT_DETAILS}-event-date-time`] as { [key: string]: string }[]) || [];
                return {
                    pxObjClass: 'IMDA-IRIS-Data-Event',
                    Performer: performers.map((row: { [key: string]: string }) => ({
                        pxObjClass: 'IMDA-IRIS-Data-Performer',
                        Name: row.artist,
                        Nationality: row.country,
                        Country: row.country,
                        GroupBandName: row['group-band'],
                        uploaded: 'true'
                    })),
                    PELicence: {
                        pxObjClass: 'IMDA-IRIS-Data-PEL',
                        PELicenceNo: eventValues[`${SECT_PE}-pe-no`],
                        PELicenceType: eventValues[`${SECT_PE}-pe-type`],
                        CompanyUEN: '', // not in mapping fields
                        PELicenceStartDate: eventValues[`${SECT_PE}-pe-start`],
                        PELicenceEndDate: eventValues[`${SECT_PE}-pe-end`],
                        Category1Public: eventValues[`${SECT_PE}-cat1`]
                    },
                    Title: eventValues[`${SECT_EVENT_DETAILS}-event-title`],
                    Type: eventValues[`${SECT_EVENT_DETAILS}-event-type`],
                    SubType: eventValues[`${SECT_EVENT_DETAILS}-event-sub-type`],
                    SubTypeDescription: eventValues[`${SECT_EVENT_DETAILS}-sub-type-desc`],
                    Language: eventValues[`${SECT_EVENT_DETAILS}-language`],
                    LanguageDescription: eventValues[`${SECT_EVENT_DETAILS}-lang-desc`],
                    WishToProvidePerformersList: eventValues[`${SECT_PERFORMER}-provide-performer-list`],
                    OfferedOnlineOrVOD: eventValues[`${SECT_EVENT_DETAILS}-offered-online`],
                    ...formatSavePlatformsToBeUsed(eventValues[`${SECT_EVENT_DETAILS}-online-platform`] as string[]),
                    OnlineComponent: eventValues[`${SECT_EVENT_DETAILS}-ticketed-or-free`],
                    OtherPlatform: eventValues[`${SECT_EVENT_DETAILS}-online-platform-others`],
                    Description: eventValues[`${SECT_EVENT_DETAILS}-event-desc`],
                    Capacity: {
                        pxObjClass: 'IMDA-IRIS-Data-Capacity',
                        AdmissionFee: eventValues[`${SECT_EVENT_DETAILS}-admission-fee`],
                        CrowdSize: eventValues[`${SECT_EVENT_DETAILS}-crowd-size`],
                        IndoorOutdoor: eventValues[`${SECT_EVENT_DETAILS}-in-out`]
                    },
                    DateAndTime: dateAndTime.map((row: { [key: string]: string }) => ({
                        pxObjClass: 'IMDA-IRIS-Data-DateAndTime',
                        StartDateTime: row.from,
                        EndDateTime: row.to,
                        uploaded: 'true'
                    })),
                    Address: [
                        {
                            pxObjClass: 'IMDA-Data-Address',
                            MailingAddress: {
                                AddressType: eventValues[`${SECT_EVENT_VENUE}-address-type`],
                                PostalCode: eventValues[`${SECT_EVENT_VENUE}-postal`],
                                BlockNumber: eventValues[`${SECT_EVENT_VENUE}-block-house`],
                                StreetName: eventValues[`${SECT_EVENT_VENUE}-street`],
                                FloorNumber: eventValues[`${SECT_EVENT_VENUE}-floor`],
                                UnitNumber: eventValues[`${SECT_EVENT_VENUE}-unit`],
                                BuildingName: eventValues[`${SECT_EVENT_VENUE}-building`],
                                LocationDescription: eventValues[`${SECT_EVENT_VENUE}-location-desc`],
                                AddressLine1: eventValues[`${SECT_EVENT_VENUE}-address1`],
                                AddressLine2: eventValues[`${SECT_EVENT_VENUE}-address2`]
                            }
                        }
                    ],
                    PerformerListDocs: attachmentPayloadSetting((eventValues[`${SECT_PERFORMER}-performerListDocs`] || []) as Attachment[]),
                    Link: eventValues[`${SECT_DOCS}-docs-list-0-link`],
                    SupportingDocuments: attachmentPayloadSetting((eventValues[`${SECT_DOCS}-docs-list-0-docs`] || []) as Attachment[]),
                    LinkOthers: eventValues[`${SECT_DOCS}-docs-list-1-link`],
                    SupportingDocumentsOthers: attachmentPayloadSetting(
                        (eventValues[`${SECT_DOCS}-docs-list-1-docs`] || []) as Attachment[]
                    )
                };
            })
            // Payment: {
            //     PaymentDescription: 'Arts Entertainment Classification Service ($30 per event)',
            //     NoOfEvents: events.length,
            //     TotalAmount: events.length * 30,
            //     pxObjClass: 'IMDA-Data-Payment'
            // }
        },
        ...(pageInstructionsForDelete.length > 0 && { pageInstructions: pageInstructionsForDelete }),
        ...(pageInstructions?.length > 0 && { pageInstructions })
    };
};

const emptySet = {
    [`${SECT_PE}-pe-no`]: '',
    [`${SECT_PE}-pe-type`]: '',
    [`${SECT_PE}-cat1`]: '',
    [`${SECT_PE}-pe-start`]: '',
    [`${SECT_PE}-pe-end`]: '',
    [`${SECT_EVENT_DETAILS}-ref-no`]: '',
    [`${SECT_EVENT_DETAILS}-event-title`]: '',
    [`${SECT_EVENT_DETAILS}-event-type`]: '',
    [`${SECT_EVENT_DETAILS}-event-sub-type`]: '',
    [`${SECT_EVENT_DETAILS}-sub-type-desc`]: '',
    [`${SECT_EVENT_DETAILS}-language`]: '',
    [`${SECT_EVENT_DETAILS}-lang-desc`]: '',
    [`${SECT_EVENT_DETAILS}-event-desc`]: '',
    [`${SECT_EVENT_DETAILS}-offered-online`]: 'No',
    [`${SECT_EVENT_DETAILS}-in-out`]: '',
    [`${SECT_EVENT_DETAILS}-event-date-time`]: [],
    [`${SECT_EVENT_DETAILS}-admission-fee`]: '',
    [`${SECT_EVENT_DETAILS}-crowd-size`]: '',
    [`${SECT_EVENT_VENUE}-address-type`]: 'SG',
    [`${SECT_EVENT_VENUE}-postal`]: '',
    [`${SECT_EVENT_VENUE}-block-house`]: '',
    [`${SECT_EVENT_VENUE}-street`]: '',
    [`${SECT_EVENT_VENUE}-floor`]: '',
    [`${SECT_EVENT_VENUE}-unit`]: '',
    [`${SECT_EVENT_VENUE}-building`]: '',
    [`${SECT_EVENT_VENUE}-address1`]: '',
    [`${SECT_EVENT_VENUE}-address2`]: '',
    [`${SECT_PERFORMER}-performer-list`]: [],
    [`${SECT_PERFORMER}-provide-performer-list`]: '',
    [`${SECT_EVENT_DETAILS}-online-platform`]: '',
    [`${SECT_EVENT_DETAILS}-ticketed-or-free`]: '',
    [`${SECT_EVENT_DETAILS}-online-platform-others`]: '',
    [`${SECT_DOCS}-docs-list-0-docs`]: []
};

const aecPayload = {
    formatRetrieve,
    formatSave,
    emptySet,
    autoFillData
};

export default aecPayload;
